import { ViewData, Answer, Question } from "@vue-skuilder/courseware";
import {
  FieldType,
  DataShape,
  DataShapeName,
  Status,
} from "@vue-skuilder/common";
import { standardFluteFingerings } from "../data/fluteFingeringChart";

/**
 * Answer format for sustained note questions
 */
export interface SustainedNoteAnswer extends Answer {
  targetNote: string;
  achieved: boolean; // Did they successfully hold the note?
  sustainedDuration: number; // Actual time sustained in ms
  avgCentsOff: number; // Average pitch deviation while sustained
  attempts: number; // Number of attempts before success
}

/**
 * Generate seed data from available flute fingerings
 * Creates cards for ALL notes in the fingering chart
 */
const generateSeedData = () => {
  const allNotes = Object.keys(standardFluteFingerings);

  const seedData: any[] = [];
  // Multiple durations for progressive difficulty
  const durations = [1000, 1500, 2000]; // 1s, 1.5s, 2s holds
  const tolerance = 50; // Standard tolerance in cents

  // Create cards for EVERY note with multiple duration variants
  allNotes.forEach((note) => {
    durations.forEach((duration) => {
      seedData.push({
        targetNote: note,
        sustainDuration: duration,
        toleranceCents: tolerance,
      });
    });
  });

  return seedData;
};

export class SustainedNoteQuestion extends Question {
  public static dataShapes: DataShape[] = [
    {
      name: "SustainedNoteQuestion" as DataShapeName,
      fields: [
        {
          name: 'targetNote',
          type: FieldType.STRING,
          validator: {
            instructions: 'Enter a note in scientific pitch notation (e.g., C4, D5, F#3)',
            test: (value: unknown) => {
              if (typeof value !== 'string') {
                return {
                  status: Status.error,
                  msg: 'Note must be a string',
                };
              }

              // Validate scientific pitch notation
              const noteRegex = /^[A-G][#b]?[0-9]$/;
              if (!noteRegex.test(value)) {
                return {
                  status: Status.error,
                  msg: 'Invalid note format. Use format like C4, D5, F#3',
                };
              }

              // Check if note exists in fingering chart
              if (!standardFluteFingerings[value]) {
                return {
                  status: Status.error,
                  msg: `Note ${value} not found in flute range`,
                };
              }

              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
        {
          name: 'sustainDuration',
          type: FieldType.NUMBER,
          validator: {
            instructions: 'How long to hold the note? (milliseconds)',
            test: (value: unknown) => {
              const num = Number(value);
              if (isNaN(num)) {
                return {
                  status: Status.error,
                  msg: 'Duration must be a number',
                };
              }
              if (num < 500 || num > 10000) {
                return {
                  status: Status.error,
                  msg: 'Duration must be between 500-10000 ms (0.5-10 seconds)',
                };
              }
              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
        {
          name: 'toleranceCents',
          type: FieldType.NUMBER,
          validator: {
            instructions: 'Pitch tolerance in cents (100 cents = 1 semitone)',
            test: (value: unknown) => {
              const num = Number(value);
              if (isNaN(num)) {
                return {
                  status: Status.error,
                  msg: 'Tolerance must be a number',
                };
              }
              if (num < 10 || num > 100) {
                return {
                  status: Status.error,
                  msg: 'Tolerance must be between 10-100 cents',
                };
              }
              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
        {
          name: 'showFingering',
          type: FieldType.STRING,
          validator: {
            instructions: 'Show fingering chart? (default: true)',
            test: (value: unknown) => {
              // Allow undefined for backward compatibility - defaults to true
              if (value === undefined || value === null) {
                return {
                  status: Status.ok,
                  msg: '',
                };
              }
              if (!(value == 'true' || value == 'false' || value == '')) {
                return {
                  status: Status.error,
                  msg: 'Must be `true` or `false`',
                };
              }
              return {
                status: Status.ok,
                msg: '',
              };
            },
          },
        },
      ],
    },
  ];

  // Note: views will be set in index.ts to avoid circular dependency
  public static views: any[] = [];
  public static acceptsUserData: boolean = true;
  public static seedData = generateSeedData();

  // Question properties
  public targetNote: string;
  public sustainDuration: number;
  public toleranceCents: number;
  public showFingering: boolean;

  constructor(data: ViewData[]) {
    super(data);
    this.targetNote = data[0].targetNote as string;
    this.sustainDuration = data[0].sustainDuration as number;
    this.toleranceCents = data[0].toleranceCents as number;
    // Default to true for backward compatibility
    this.showFingering = data[0].showFingering !== undefined
      ? (data[0].showFingering as string) === 'true'
      : true;
  }

  public dataShapes(): DataShape[] {
    return SustainedNoteQuestion.dataShapes;
  }

  public views() {
    return SustainedNoteQuestion.views;
  }

  /**
   * Evaluate the answer with time-based performance
   */
  public evaluate(a: Answer, t: number) {
    return {
      isCorrect: this.isCorrect(a),
      performance: this.displayedSkill(a as SustainedNoteAnswer, t),
    };
  }

  /**
   * Check if the note was successfully sustained
   */
  protected isCorrect(answer: Answer): boolean {
    const a = answer as SustainedNoteAnswer;
    return a.achieved === true;
  }

  /**
   * Calculate performance score based on pitch accuracy and attempts
   * Returns a value between 0 and 1
   */
  protected displayedSkill(a: SustainedNoteAnswer, t: number): number {
    if (!a.achieved) {
      return 0;
    }

    let score = 1.0;

    // Penalty for pitch deviation (more than 5 cents off target)
    if (a.avgCentsOff > 5) {
      // Scale from 1.0 at 5 cents to 0.5 at 50 cents
      const deviationPenalty = Math.min(0.5, ((a.avgCentsOff - 5) / 45) * 0.5);
      score -= deviationPenalty;
    }

    // Penalty for multiple attempts
    if (a.attempts > 1) {
      // Each additional attempt reduces score by 10%, down to minimum of 0.7
      const attemptPenalty = Math.min(0.3, (a.attempts - 1) * 0.1);
      score -= attemptPenalty;
    }

    // Ensure score stays in valid range
    return Math.max(0, Math.min(1, score));
  }
}
